import {
  Input,
  QueryList,
  Directive,
  ContentChildren,
}
  from "@angular/core";

/**
 * interfaces
 */
import { IFormFieldComponentProps } from "@sharedModules/forms/interfaces/form-field-component-props";
import { IFormFieldFeedbackProps } from "@sharedModules/forms/directives/form-field-feedback/interfaces/form-field-feedback";

/**
 * enums
 */
import { IFormFieldSizeType } from "@sharedModules/forms/enums/form-field-size";
import { FormFieldFeedbackStatus, IFormFieldFeedbackStatus } from "@sharedModules/forms/directives/form-field-feedback/enums/form-field-feedback-type";
import { FormFieldFeedbackProvider, IFormFieldFeedbackProvider } from "@sharedModules/forms/directives/form-field-feedback/enums/form-field-feedback-provider";

/**
 * models
 */
import { FormFieldControlBase } from "../models/form-field-control/form-field-control-model";

/**
 * directives
 */
import { TemplateDirective } from "@sharedDirectives/templates/template.directive";
import { IFormFieldStatus } from "@sharedModules/forms/directives/form-field-status/enums/form-field-status";

/**
 * @directive FormFieldHighOrderDirectiveBase
 * @description
 * Abstract high-order directive that extends FormFieldControlBase with UI composition, template management, and component properties.
 * Provides a foundation for form field components with template-based UI composition and comprehensive property configuration.
 * This directive implements the composition pattern for form field UI, allowing flexible template injection and consistent styling.
 *
 * @template T The value type of the form field (default: unknown).
 * @extends FormFieldControlBase<T>
 * @implements IFormFieldComponentProps, IFormFieldFeedbackProps
 *
 * @example
 * export class CustomFieldComponent extends FormFieldHighOrderDirectiveBase<string> {
 *   // Component-specific implementation
 *   // Inherits all UI composition features
 * }
 *
 * @example
 * <app-custom-field>
 *   <ng-template appTemplate="label">Custom Label</ng-template>
 *   <ng-template appTemplate="help">Help Text</ng-template>
 * </app-custom-field>
 */
@Directive()
export abstract class FormFieldHighOrderDirectiveBase<T = unknown>
  extends FormFieldControlBase<T>
  implements IFormFieldComponentProps, IFormFieldFeedbackProps {

  /**
   * Content children templates for flexible UI composition.
   * Allows injection of custom templates for label, help text, and other UI elements.
   * 
   * @contentChildren TemplateDirective
   */
  @ContentChildren(TemplateDirective)
  public templates!: QueryList<TemplateDirective>

  /**
   * Unique identifier of the form field.
   * Used to associate the label with the input and for accessibility purposes.
   * 
   * @input identifier
   * @required
   */
  @Input({ alias: 'identifier', required: true })
  public id!: string;

  /**
   * Name attribute for the input.
   * Used for form submission and accessibility.
   * 
   * @input name
   * @required
   */
  @Input({ alias: 'name', required: true })
  public name!: string;

  /**
   * Optional placeholder for the input.
   * Provides hint text to guide user input.
   * 
   * @input placeholder
   */
  @Input({ alias: 'placeholder' })
  public placeholder: string | undefined = '';

  /**
   * Whether the input is readonly.
   * If true, the field cannot be edited but can still be focused.
   * 
   * @input readonly
   */
  @Input({ alias: 'readonly' })
  public readonly: boolean = false;

  /**
   * Whether the input is disabled.
   * If true, the field is not interactive and excluded from form submission.
   * 
   * @input disabled
   */
  @Input({ alias: 'disabled' })
  public disabled: boolean = false;

  /**
   * Whether the input is required.
   * If true, the field must be filled for form validity.
   * 
   * @input required
   */
  @Input({ alias: 'required' })
  public required: boolean = false;

  /**
   * Optional label to be displayed with the field.
   * Provides descriptive text for the form field.
   * 
   * @input label
   */
  @Input({ alias: 'label' })
  public label: string | undefined;

  /**
   * Whether to show the label.
   * 
   * @input showLabel
   * @default true
   */
  @Input({ alias: 'showLabel' })
  public showLabel: boolean = true;

  /**
   * ARIA label for accessibility.
   * Provides screen reader support for the form field.
   * 
   * @input ariaLabel
   */
  @Input({ alias: 'ariaLabel' })
  public ariaLabel: string | undefined;

  /**
   * Optional description ID for ARIA accessibility.
   * Links to a description element for screen readers.
   * 
   * @input ariaDescribed
   */
  @Input({ alias: 'ariaDescribed' })
  public described: string | undefined

  /**
   * Label class styling.
   * Allows custom CSS classes for label styling.
   * 
   * @input labelClassStyle
   */
  @Input({ alias: 'labelClassStyle' })
  public labelClassStyle: string | string[] | undefined;

  /**
   * Field size for responsive styling.
   * Controls the visual size of the form field.
   * 
   * @input size
   * @setter
   */
  @Input({ alias: 'size' })
  public size!: IFormFieldSizeType

  /**
   * Help text for the form field.
   * Provides additional guidance to users.
   * 
   * @input textHelp
   */
  @Input({ alias: 'textHelp' })
  public textHelp: string | undefined

  /**
   * Help text class styling.
   * Allows custom CSS classes for help text styling.
   * 
   * @input textHelpClassStyle
   */
  @Input({ alias: 'textHelpClassStyle' })
  public textHelpClassStyle: string | string[] = ''

  /**
   * Status to ignore for field validation display.
   * Controls which validation states are hidden.
   * 
   * @input ignoreStatus
   */
  @Input({ alias: 'ignoreStatus' })
  public ignoreStatus!: IFormFieldStatus

  /**
   * Feedback type for validation messages.
   * Controls what type of feedback is displayed.
   * 
   * @input feedbackStatus
   */
  @Input({ alias: 'feedbackStatus' })
  public feedbackStatus: IFormFieldFeedbackStatus = FormFieldFeedbackStatus.Invalid

  /**
   * Feedback provider for validation display.
   * Controls how feedback is displayed (tooltip, inline, etc.).
   * 
   * @input feedbackProvider
   */
  @Input({ alias: 'feedbackProvider' })
  public feedbackProvider: IFormFieldFeedbackProvider = FormFieldFeedbackProvider.Default

  /**
   * Whether to show full feedback UI.
   * Controls the completeness of feedback display.
   * 
   * @input feedbackFullUI
   */
  @Input({ alias: 'feedbackFullUI' })
  public feedbackFullUI: boolean = true

  /**
   * Custom valid message for feedback.
   * Overrides the default valid message.
   * 
   * @input feedbackValidMsg
   */
  @Input({ alias: 'feedbackValidMsg' })
  public feedbackValidMsg: string = ''

  /**
   * Determines if feedback provider is tooltip.
   * Returns true if feedback is displayed as tooltip.
   * 
   * @readonly
   * @returns {boolean} True if tooltip provider is used
   */
  public get isFeedbackProviderTooltip(): boolean {
    return this.feedbackProvider === FormFieldFeedbackProvider.Tooltip
  }

  /**
   * Dynamic feedback UI configuration.
   * Provides the appropriate feedback type based on field state and configuration.
   * 
   * @readonly
   * @returns {IFormFieldFeedbackStatus} The appropriate feedback type
   */
  public get dynamicFeedbackUI(): IFormFieldFeedbackStatus {
    if (this.feedbackFullUI) {
      return this.valid ? FormFieldFeedbackStatus.Valid : FormFieldFeedbackStatus.Invalid;
    }
    return this.feedbackStatus;
  }

  /**
   * Feedback context for template injection.
   * Provides data for feedback template rendering.
   * 
   * @readonly
   * @returns {object} Feedback context object
   */
  public get feedbackContext() {
    return {
      valid: this.valid,
      errors: this.errors,
      invalid: this.invalid,
      touched: this.touched,
      validMsg: this.feedbackValidMsg,
      $implicit: this.handlerErrorValue || this.feedbackValidMsg,
    }
  }

  /**
   * Gets a template by name from the content children.
   * Useful for accessing injected templates programmatically.
   * 
   * @param tempName The name of the template to find
   * @returns {TemplateDirective | undefined} The found template or undefined
   */
  public getTemplate(tempName: string): TemplateDirective | undefined {
    return this.templates?.find(template => template.name === tempName);
  }

}
